import { useToast } from '@chakra-ui/react';
import Cookies from 'js-cookie';
import { createContext, useContext, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ProtectedRoutes } from '@/routes/protected.routes';
import { PublicRoutes } from '@/routes/public.routes';

type AppRoutesData = {
  activeRouter: typeof ProtectedRoutes;
  changeRouter(router: typeof ProtectedRoutes): void;
};

export const AppRoutesContext = createContext<AppRoutesData>(
  {} as AppRoutesData,
);

const AppRouterProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('login/signIn');
  const toast = useToast();

  const [activeRouter, setActiveRouter] = useState(() => {
    const accessToken = Cookies.get('accessToken');
    const userWasLoggedIn = Cookies.get('userType');

    if (accessToken) {
      return ProtectedRoutes;
    }

    if (userWasLoggedIn) {
      toast({
        title: t('sessionExpired'),
        description: t('signInToContinue'),
        status: 'info',
        duration: 6000,
        isClosable: true,
      });
    }

    return PublicRoutes;
  });

  const changeRouter = (router: typeof ProtectedRoutes) => {
    setActiveRouter(router);
  };

  return (
    <AppRoutesContext.Provider value={{ activeRouter, changeRouter }}>
      {children}
    </AppRoutesContext.Provider>
  );
};

function useAppRouter() {
  const context = useContext(AppRoutesContext);

  if (!context) {
    throw new Error('useAppRouter must be used within AppRouterProvider');
  }

  return context;
}

export { useAppRouter, AppRouterProvider };
